/**
 * Панель фильтров задачника (§16.2).
 *
 * Все значения приходят из URL и уходят в URL — состояние здесь не живёт.
 * Счётчик активных фильтров и кнопка сброса обязательны: без них
 * пользователь не понимает, почему доска выглядит полупустой.
 *
 * На телефоне панель целиком свёрнута под кнопку «Фильтры · N»
 * (PhoneTaskbookBar), поэтому развёрнутая показывает всё сразу, столбиком:
 * второе окно поверх уже раскрытой панели было бы лишним шагом.
 */

import { type ReactNode, useState } from "react";
import { CalendarRange, FilterX } from "lucide-react";

import { useIsPhone } from "@/shared/lib/media";
import {
  Button,
  Checkbox,
  cn,
  DateRangeInput,
  MultiSelectPicker,
  type PickerOption,
  Popover,
  SearchInput,
  Tag,
} from "@/shared/ui";

import {
  type FilterValidation,
  NO_ASSIGNEE,
  NO_ASSIGNEE_LABEL,
  type PeriodMode,
  type TaskFilterState,
} from "@taskflow/client-core/tasks";

interface Props {
  filters: TaskFilterState;
  validation: FilterValidation;
  activeCount: number;
  setFilter: <K extends keyof TaskFilterState>(key: K, value: TaskFilterState[K]) => void;
  /** Несколько фильтров одним обновлением адреса — для диапазона срока. */
  setFilters: (patch: Partial<TaskFilterState>) => void;
  reset: () => void;
  assignees: PickerOption[];
  priorities: PickerOption[];
  types: PickerOption[];
  /** Теги принадлежат доске: в списке нескольких досок фильтра по ним нет. */
  tags?: PickerOption[];
  /** Быстрые фильтры в начале строки — например, «Мои сегодня». */
  leading?: ReactNode;
  /** Локальное значение поля поиска: ввод не должен дёргать URL на каждый символ. */
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  /** Подпись поля поиска для скринридера: в архиве ищут не по доске. */
  searchLabel?: string;
  /** «Просроченные» не имеет смысла там, где работа уже закончена, — в архиве. */
  showOverdue?: boolean;
  /** Что значит период: срок задачи или период работы (filtersToQuery). */
  periodMode?: PeriodMode;
}

export function TaskFilterBar({
  filters,
  validation,
  activeCount,
  setFilter,
  setFilters,
  reset,
  assignees,
  priorities,
  types,
  tags,
  leading,
  searchInput,
  onSearchInputChange,
  searchLabel = "Поиск по задачам доски",
  showOverdue = true,
  periodMode = "due",
}: Props) {
  const phone = useIsPhone();

  const rangeLabel =
    filters.dueFrom === null && filters.dueTo === null
      ? periodMode === "work"
        ? "Любой период работы"
        : "Любой срок"
      : `${filters.dueFrom ?? "…"} — ${filters.dueTo ?? "…"}`;

  const resetAll = () => {
    onSearchInputChange("");
    reset();
  };

  const search = (
    <SearchInput
      label={searchLabel}
      placeholder="Номер или название"
      value={searchInput}
      onChange={onSearchInputChange}
      onSearch={(value) => setFilter("search", value)}
    />
  );

  /** Фильтры кроме поиска: в строке — фиксированной ширины, в окне — столбиком. */
  const controls = (stacked: boolean) => (
    <>
      <div className={stacked ? "w-full" : "w-52"}>
        <MultiSelectPicker
          label="Фильтр по исполнителям"
          placeholder="Исполнители"
          // «Без исполнителя» — первым: он не человек и в алфавит не входит.
          options={[{ id: NO_ASSIGNEE, label: NO_ASSIGNEE_LABEL }, ...assignees]}
          value={filters.assigneeIds}
          onChange={(value) => setFilter("assigneeIds", value)}
        />
      </div>

      <div className={stacked ? "w-full" : "w-44"}>
        <MultiSelectPicker
          label="Фильтр по приоритету"
          placeholder="Приоритет"
          options={priorities}
          value={filters.priorityIds}
          onChange={(value) => setFilter("priorityIds", value)}
        />
      </div>

      <div className={stacked ? "w-full" : "w-44"}>
        <MultiSelectPicker
          label="Фильтр по типу задачи"
          placeholder="Тип"
          options={types}
          value={filters.typeIds}
          onChange={(value) => setFilter("typeIds", value)}
        />
      </div>

      {tags !== undefined && (
        <div className={stacked ? "w-full" : "w-44"}>
          <MultiSelectPicker
            label="Фильтр по тегам"
            placeholder="Теги"
            options={tags}
            value={filters.tagIds}
            onChange={(value) => setFilter("tagIds", value)}
          />
        </div>
      )}

      {/* §16.3: произвольный период «с — по». */}
      <Popover
        trigger={
          <Button
            variant="secondary"
            size="sm"
            icon={CalendarRange}
            className={cn(
              validation.invalidRange && "border-danger",
              stacked && "w-full justify-start",
            )}
            title={
              periodMode === "work"
                ? "Задачи, выполненные в периоде или бывшие в работе, — как в «Отчёте по работам»"
                : undefined
            }
          >
            {rangeLabel}
          </Button>
        }
      >
        {({ close }) => (
          <DueRangeForm
            periodMode={periodMode}
            from={filters.dueFrom}
            to={filters.dueTo}
            onApply={(from, to) => {
              // Обе границы — одним обновлением: два вызова setFilter
              // подряд затирали друг друга, и «с» терялось.
              setFilters({ dueFrom: from, dueTo: to });
              close();
            }}
          />
        )}
      </Popover>

      {showOverdue && (
        <Checkbox
          label="Просроченные"
          checked={filters.overdueOnly}
          onChange={(value) => setFilter("overdueOnly", value)}
        />
      )}

      <Checkbox
        label="Мои задачи"
        checked={filters.mineOnly}
        onChange={(value) => setFilter("mineOnly", value)}
      />
    </>
  );

  if (phone) {
    return (
      <div className="flex flex-col gap-2 border-b border-line bg-surface px-4 py-2.5">
        {leading !== undefined && <div className="flex flex-wrap items-center gap-2">{leading}</div>}
        {search}
        {controls(true)}
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" icon={FilterX} onClick={resetAll} className="self-start">
            Сбросить фильтры
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 border-b border-line bg-surface px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        {leading}

        <div className="w-64">{search}</div>

        {controls(false)}

        {activeCount > 0 && (
          <>
            <Tag tone="accent" size="sm">
              {`Фильтров: ${String(activeCount)}`}
            </Tag>
            <Button size="sm" variant="ghost" icon={FilterX} onClick={resetAll}>
              Сбросить
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * §25.5: даты выбираются в черновике и применяются кнопкой «Применить».
 * Прежде выборка перестраивалась на каждое нажатие в календаре, а
 * «Сбросить» внутри окна не было — хотя его требуют и макет, и §25.5.
 */
function DueRangeForm({
  periodMode,
  from,
  to,
  onApply,
}: {
  periodMode: PeriodMode;
  from: string | null;
  to: string | null;
  onApply: (from: string | null, to: string | null) => void;
}) {
  const [draftFrom, setDraftFrom] = useState(from ?? "");
  const [draftTo, setDraftTo] = useState(to ?? "");
  // ISO-даты сравниваются как строки: ГГГГ-ММ-ДД упорядочены лексикографически.
  const invalid = draftFrom !== "" && draftTo !== "" && draftFrom > draftTo;

  return (
    <div className="flex w-72 flex-col gap-2">
      <div className="flex flex-col gap-1 text-xs font-semibold text-muted">
        <span aria-hidden>{periodMode === "work" ? "Период работы" : "Период"}</span>
        {/* Можно задать и одну границу — «с» или «по»: напечатать её в поле. */}
        <DateRangeInput
          typeable
          aria-label={periodMode === "work" ? "Период работы" : "Срок"}
          from={draftFrom}
          to={draftTo}
          invalid={invalid}
          onChange={(range) => {
            setDraftFrom(range.from);
            setDraftTo(range.to);
          }}
        />
      </div>
      {invalid && (
        <p role="alert" className="text-[11px] font-semibold text-danger">
          Начало периода не может быть позже окончания
        </p>
      )}
      <div className="mt-1 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => onApply(null, null)}>
          Сбросить
        </Button>
        <Button
          size="sm"
          disabled={invalid}
          onClick={() => onApply(draftFrom === "" ? null : draftFrom, draftTo === "" ? null : draftTo)}
        >
          Применить
        </Button>
      </div>
    </div>
  );
}
